import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FaTachometerAlt, FaUsers, FaCheckCircle,
  FaHistory, FaUserShield, FaSignOutAlt, FaComments,
  FaBars, FaTimes
} from 'react-icons/fa';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../utils/config';
import API from '../../utils/api';

// Sub pages
import DashboardHome from './DashboardHome';
import AllRegistrations from './AllRegistrations';
import RegistrationForm from './RegistrationForm';
import ManageAdmins from './ManageAdmins';
import ActivityLogs from './ActivityLogs';
import AdminChat from './AdminChat';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  useEffect(() => {
    const fetchUnread = () => {
      API.get('/messages').then(res => {
        const msgs = res.data || [];
        const lastRead = localStorage.getItem('last_admin_read_msg_id') || '0';
        const unread = msgs.filter(m => {
          const sRole = m.sender_role || m.senderRole;
          return sRole === 'user' && Number(m.id) > Number(lastRead);
        }).length;

        if (location.pathname === '/admin/chat') {
          setUnreadMessages(0);
        } else {
          setUnreadMessages(unread);
        }
      }).catch(() => {});
    };

    fetchUnread();

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('join_room', { userId: String(user?.id), role: 'admin', name: user?.username });
    });

    socket.on('receive_message', (msg) => {
      const sRole = msg.senderRole || msg.sender_role;
      if (sRole === 'user') {
        if (window.location.pathname === '/admin/chat') {
          localStorage.setItem('last_admin_read_msg_id', String(msg.id));
          setUnreadMessages(0);
        } else {
          setUnreadMessages(prev => prev + 1);
        }
      }
    });

    return () => socket.disconnect();
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/admin/chat') {
      API.get('/messages').then(res => {
        const msgs = res.data || [];
        if (msgs.length > 0) {
          const maxId = Math.max(...msgs.map(m => Number(m.id) || 0));
          localStorage.setItem('last_admin_read_msg_id', String(maxId));
        }
        setUnreadMessages(0);
      }).catch(() => {});
    }
  }, [location.pathname]);

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: <FaTachometerAlt size={16} /> },
    { path: '/admin/registrations', label: 'All Registrations', icon: <FaUsers size={16} /> },
    { path: '/admin/register', label: 'New Registration', icon: <FaCheckCircle size={16} /> },
    { path: '/admin/chat', label: 'Messages', icon: <FaComments size={16} />, badge: unreadMessages },
    ...(isSuperAdmin ? [
      { path: '/admin/admins', label: 'Manage Admins', icon: <FaUserShield size={16} /> },
      { path: '/admin/activity', label: 'Activity Logs', icon: <FaHistory size={16} /> },
    ] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="dashboard-layout">
      {/* Mobile Header Bar */}
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
          <img src="/logo.png" alt="Sri Karunasai Logo" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'contain' }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>Admin Panel</span>
        </div>
        <button
          className="mobile-logout-btn"
          onClick={handleLogout}
          title="Logout"
        >
          <FaSignOutAlt size={14} /> Logout
        </button>
      </div>

      {/* Mobile Drawer Backdrop */}
      {mobileMenuOpen && (
        <div
          className="mobile-drawer-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Sri Karunasai Logo" className="sidebar-logo-img" />
          <div className="sidebar-logo-text">
            <h3>Sri Karunasai</h3>
            <span>Admin Panel</span>
          </div>
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <div className="user-avatar">
            {(user?.username || 'A').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{user?.username}</div>
            <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 500 }}>
              {isSuperAdmin ? 'Super Admin' : 'Administrator'}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          <div className="nav-section-title">Navigation</div>
          {navItems.map(item => (
            <div
              key={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div
            className="nav-item"
            onClick={handleLogout}
            style={{ color: '#ef4444' }}
          >
            <FaSignOutAlt size={14} /> Logout
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Routes>
          <Route path="dashboard" element={<DashboardHome onStatsLoad={setStats} />} />
          <Route path="registrations" element={<AllRegistrations />} />
          <Route path="register" element={<RegistrationForm />} />
          <Route path="chat" element={<AdminChat />} />
          <Route
            path="admins"
            element={isSuperAdmin ? <ManageAdmins /> : <Navigate to="/admin/dashboard" replace />}
          />
          <Route
            path="activity"
            element={isSuperAdmin ? <ActivityLogs /> : <Navigate to="/admin/dashboard" replace />}
          />
          <Route path="*" element={<DashboardHome onStatsLoad={setStats} />} />
        </Routes>
      </main>
    </div>
  );
}
